import "../styles/pdsProduktbanner.scss";
import VariantenwechselLogic, { VariantenWechselDetails, variantenWechselEvent } from "./VariantenwechselLogic";
import * as CubeTrackingAK from "./tracking/cube/AktionskommunikationsTracking";
import { middle, middleDetails, productbanner } from "./CAMPWindow";
import * as CubeTrackingAB from "./tracking/cube/AbTestTracking";
import * as GtmTrackingAB from "./tracking/gtm/AbTestTracking";
import { CampModal } from "./CampModal";
import "./CampModal";

declare global {
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface DocumentEventMap {
        [variantenWechselEvent]: CustomEvent<VariantenWechselDetails>;
    }
}

class PdsProduktbanner extends HTMLElement {
    private readonly produktbannerLogic: VariantenwechselLogic;
    private infoTextTrigger: HTMLElement | undefined | null;
    private infoTextModal: CampModal | undefined | null;
    private kampagneName: string | undefined | null;
    private observer: IntersectionObserver;
    private alreadyTracked: boolean;
    private internalEventsAbortController: AbortController | undefined;
    private variantenWechselEventAbortController: AbortController;

    constructor() {
        super();
        this.produktbannerLogic = new VariantenwechselLogic(this.replaceProduktbannerContent.bind(this), fetch);
        this.observer = new IntersectionObserver(this.handleObservationEvent.bind(this), { threshold: 0.9 });
        this.alreadyTracked = false;
        this.variantenWechselEventAbortController = new AbortController();
    }

    connectedCallback(): void {
        const pathPrefix = this.getAttribute("data-path-prefix");
        const ajaxPath = this.getAttribute("data-ajax-path");
        if (pathPrefix && ajaxPath) {
            this.produktbannerLogic.setPathPrefix(pathPrefix);
            this.produktbannerLogic.setAjaxPath(ajaxPath);
            document.addEventListener(variantenWechselEvent, this.handleVariantenWechsel.bind(this), {
                passive: true,
                signal: this.variantenWechselEventAbortController.signal,
            });
        }
        this.setupDOMEventHandling();
    }

    private replaceProduktbannerContent(content: string): void {
        this.internalEventsAbortController?.abort();
        this.observer.unobserve(this);
        this.alreadyTracked = false;
        this.innerHTML = content;
        this.setupDOMEventHandling();
    }

    private handleVariantenWechsel(event: CustomEvent<VariantenWechselDetails>): void {
        void this.produktbannerLogic.handleEvent(event);
    }

    private handleObservationEvent(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry) => {
            if (entry.isIntersecting && !this.alreadyTracked && this.kampagneName) {
                CubeTrackingAK.trackView(this.kampagneName, middle, productbanner);
                const el = this.querySelector<HTMLDivElement>('[data-select="banner"]');
                const abTestHeaderName = el?.getAttribute("data-abtest-name");
                const abTestHeaderValue = el?.getAttribute("data-abtest-value");
                CubeTrackingAB.trackView(abTestHeaderValue, abTestHeaderName);
                GtmTrackingAB.trackView(abTestHeaderValue, abTestHeaderName);
                this.alreadyTracked = true;
            }
        });
    }

    disconnectedCallback(): void {
        this.observer.disconnect();
        this.internalEventsAbortController?.abort();
        this.variantenWechselEventAbortController.abort();
    }

    private setupInfoLayer(signal: AbortSignal): void {
        this.infoTextModal = this.querySelector<CampModal>("#pdsproduktbanner-infotext");
        this.infoTextTrigger = this.querySelector<HTMLElement>("#pdsproduktbanner-infotext-trigger");
        this.infoTextTrigger?.addEventListener("click", this.handleInfoLayerOpenClick.bind(this), { signal });
    }

    private setupLinkClickDetail(signal: AbortSignal): void {
        const link = this.querySelector<HTMLAnchorElement>('[data-select="detail-link"]');
        if (link) {
            link.addEventListener(
                "click",
                (event) => {
                    const target = event.target as HTMLAnchorElement;
                    if (target && this.kampagneName) {
                        CubeTrackingAK.trackLinkClick(this.kampagneName, target.href, middleDetails, productbanner);
                    }
                },
                { signal },
            );
        }
    }

    private handleInfoLayerOpenClick(): void {
        if (this.kampagneName) {
            CubeTrackingAK.trackDetailClick(this.kampagneName, middle, productbanner);
        }
        const movedModal = document.querySelector<CampModal>("#pdsproduktbanner-infotext");
        movedModal?.classList.remove("display-none");
        movedModal?.show();
    }

    private setupArrowClick(signal: AbortSignal): void {
        const link = this.querySelector<HTMLAnchorElement>('[data-select="link"]');
        if (link) {
            link.addEventListener(
                "click",
                (event) => {
                    const target = event.target as HTMLAnchorElement;
                    if (target && this.kampagneName) {
                        CubeTrackingAK.trackLinkClick(this.kampagneName, target.href, middle, productbanner);
                    }
                },
                { signal },
            );
        }
    }

    private setupDOMEventHandling(): void {
        this.internalEventsAbortController = new AbortController();
        this.kampagneName = this.querySelector<HTMLDivElement>('[data-select="banner"]')?.getAttribute("data-kampagne-name");
        const signal = this.internalEventsAbortController.signal;
        this.setupLinkClickDetail(signal);
        this.setupArrowClick(signal);
        this.setupInfoLayer(signal);
        this.observer.observe(this);
    }
}

customElements.define("entd-pdsproduktbanner", PdsProduktbanner);
